import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Stack, Paper, InputLabel, Alert, CircularProgress } from '@mui/material';
import { getApiBase } from '../utils/apiConfig';

const SingleItemRequestForm = () => {
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [item, setItem] = useState('');
  const [date, setDate] = useState('');
  const [photo, setPhoto] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('pickup', pickup);
      formData.append('dropoff', dropoff);
      formData.append('item', item);
      formData.append('date', date);
      formData.append('notes', notes);
      if (photo) formData.append('photo', photo);
      const res = await fetch(`${getApiBase()}/single-item-requests`, { method: 'POST', body: formData });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6, minHeight: '100vh' }}>
      <Typography variant="h3" fontWeight={800} mb={4} align="center">
        Request a Single Item Move
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, justifyContent: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 340, maxWidth: 500 }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            {submitted ? (
              <Alert severity="success" sx={{ fontSize: 18, py: 4, textAlign: 'center' }}>
                Thank you! Your request has been received.<br />
                Our team will match you with a mover and contact you soon.
              </Alert>
            ) : (
              <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField label="Pickup Address" value={pickup} onChange={e => setPickup(e.target.value)} fullWidth required />
                  <TextField label="Dropoff Address" value={dropoff} onChange={e => setDropoff(e.target.value)} fullWidth required />
                  <TextField label="Item Description" value={item} onChange={e => setItem(e.target.value)} fullWidth required placeholder="e.g. Piano, Couch, Fridge" />
                  <TextField label="Preferred Date" type="date" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />
                  <Box>
                    <InputLabel shrink>Photo (optional)</InputLabel>
                    <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} />
                  </Box>
                  <TextField label="Special Instructions" value={notes} onChange={e => setNotes(e.target.value)} fullWidth multiline minRows={2} />
                  {error && <Alert severity="error">{error}</Alert>}
                  <Button variant="contained" size="large" type="submit" disabled={loading}>
                    {loading ? <CircularProgress size={22} /> : 'Request Pickup'}
                  </Button>
                </Stack>
              </form>
            )}
          </Paper>
        </Box>
        {/* Sidebar Info/FAQ */}
        <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
          <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, boxShadow: 1, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>How It Works</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              1. Fill out the request form with your pickup/dropoff details and item info.<br />
              2. Our team will match you with a trusted mover.<br />
              3. You’ll get a quote and can confirm your booking.<br />
              4. Pay securely after your item is delivered.
            </Typography>
          </Box>
          <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, boxShadow: 1 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>FAQ</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}><b>What items can I move?</b> Any single household item (furniture, appliance, etc.).</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}><b>How soon will I get a quote?</b> Usually within 1 business day.</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}><b>Are movers insured?</b> Yes, all movers are vetted and insured.</Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default SingleItemRequestForm;
